import { useEffect, useState } from "react";
import { Navigate, useNavigate, Link } from "react-router";
import { useAppDispatch, useAppSelector } from "../../state/store";
import { Breadcrumbs, Grid } from "@mui/material";
import { HelpButton } from "../../components/HelpButton";
import { SKSettings } from "./SKSettings";
import { Row } from "../../components/Row";
import { StringInput } from "../../components/Inputs";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { useKeyedParams } from "../../hooks/useParams";
import { useRequestHandler } from "../../hooks/requestHandler";
import { useDataProvider } from "../../hooks/dataProvider";
import {
  createSeason,
  getQuiz,
  getSeasonInfo,
  setSelectedSeason,
  updateSeason,
} from "../../state/actions/skActions";
import * as skTypes from "../../types/sk";
import {
  addSeasonToLocalStore,
  getLocalQuiz,
  getLocalSeason,
  setLocalQuiz,
} from "../../util/skUtils";
import { SET_SK_EVENT } from "../../state/reducers/sk";
import { ReactComponent as HomePage } from "../../icons/tournamentIcons/homepage.svg";
import { ReactComponent as SettingsIcon } from "../../icons/mmButtonIcons/settings.svg";
import "./SK.scss";

interface SKMenuProps {
  seasonId: string;
  eventId: string;
}

export function SKMenu() {
  const { seasonId, eventId } = useKeyedParams<SKMenuProps>();

  const provider = useDataProvider({
    id: seasonId,
    getId: (season) => season.seasonId,
    dispatchAction: setSelectedSeason,
    getData: getSeasonInfo,
    selector: (state) => state.sk.selectedSeason,
    cacheDetails: {
      prefix: "season",
      get: getLocalSeason,
      save: addSeasonToLocalStore,
    },
    render: (selectedSeason) => {
      return (
        <SKMenuPage
          selectedSeason={selectedSeason}
          eventId={eventId}
          seasonId={seasonId}
        />
      );
    },
  });
  return provider.getPage();
}

export function SKMenuPage(props: {
  selectedSeason: skTypes.Season;
  seasonId: string;
  eventId: string;
}) {
  const { selectedSeason, eventId, seasonId } = props;
  useDocumentTitle(`${selectedSeason.seasonName} - BQA Scorekeeper`);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const settings = useAppSelector((state) => state.settings);
  const { user } = useAppSelector((state) => state.authentication);

  const [editEventName, setEditEventName] = useState<string>("");
  const [editingEvent, setEditingEvent] = useState<boolean>(false);
  const [showStatsButtons, setShowStatsButtons] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    dispatch({
      type: SET_SK_EVENT,
      payload: {
        selectedEvent: eventId,
      },
    });
  }, []);

  useEffect(() => {
    if (props.selectedSeason.location === "LOCAL" && user)
      dispatch(createSeason(props.selectedSeason));
  }, []);

  const handler = useRequestHandler({
    onSuccess: (key) => {
      switch (key) {
        case "EVENT_NAME":
          setEditingEvent(false);
          break;
        case "EVENT_DELETE":
          navigate(`/sk/${seasonId}/events`);
      }
    },
    assumeCachedInfo: true,
  });

  const event = selectedSeason.events.find((ev) => ev.eventId === eventId);

  // Check for quizzes not pulled locally
  useEffect(() => {
    if (event && user) {
      event.quizzes.forEach((summary) => {
        const pullQuiz = async () => {
          const dbQuiz = await getQuiz(summary.quizId);
          if (dbQuiz) setLocalQuiz(dbQuiz);
        };
        const localQuiz = getLocalQuiz(summary.quizId);
        if (!localQuiz) pullQuiz();
      });
    }
  }, []);

  if (!event) return <Navigate to={`/sk/${seasonId}/events`} />;

  const quizBreakpoints = {
    xs: 6,
    sm: 4,
    md: 3,
    lg: 2,
    xl: 2,
  };
  return (
    <div className="page">
      <Grid container spacing={[0, 2]} style={{ marginBottom: 10 }}>
        <Grid>
          <h1>{event.eventName}</h1>
        </Grid>

        {!editingEvent && (
          <Grid style={{ marginTop: 6 }}>
            <button
              className="link"
              onClick={() => {
                setEditingEvent(true);
                setEditEventName(event?.eventName || "");
              }}
            >
              Edit Event Name
            </button>
          </Grid>
        )}
      </Grid>
      <Grid container>
        <Grid>
          <Breadcrumbs
            separator="/"
            sx={{
              li: { listStyle: "none", marginLeft: 0.5, marginRight: 0.5 },
            }}
          >
            <Link className="link" to="/" aria-label="BQA Home">
              <HomePage width={14} height={14} />
            </Link>
            <Link className="link" to="/sk/seasons">
              Seasons
            </Link>
            <Link className="link" to={`/sk/${selectedSeason.seasonId}/events`}>
              {selectedSeason.seasonName}
            </Link>
            <span>{event.eventName}</span>
          </Breadcrumbs>
        </Grid>
        <Grid offset="auto">
          {!user && (
            <span className="important-information" style={{ marginRight: 20 }}>
              Logged Out
            </span>
          )}
          <HelpButton tab="starting" />
          {!showSettings && (
            <button
              className="link"
              onClick={() => {
                setShowSettings(true);
                setShowStatsButtons(false);
              }}
              style={{ marginLeft: 20 }}
              aria-label="Scorekeeper Settings"
            >
              <SettingsIcon width={24} height={24} />
            </button>
          )}
        </Grid>
      </Grid>

      <div className="main-box" style={{ marginTop: 20 }}>
        {editingEvent && (
          <div style={{ display: "flex" }}>
            <StringInput
              caption="Edit Event Name:"
              style={{ marginTop: 10, maxWidth: "unset" }}
              value={editEventName}
              onChange={setEditEventName}
              data-testid="sk-change-event-name"
              spacing={false}
              afterInput={
                <button
                  className="clickable"
                  style={{
                    marginLeft: 15,
                    visibility:
                      editEventName !== event?.eventName ? "visible" : "hidden",
                  }}
                  onClick={() => {
                    const eventIndex = selectedSeason?.events.findIndex(
                      (event: skTypes.Event) => event.eventId === eventId
                    );
                    if (
                      eventIndex !== undefined &&
                      eventIndex >= 0 &&
                      selectedSeason
                    ) {
                      const newSeason: skTypes.Season = {
                        ...selectedSeason,
                        events: [...(selectedSeason?.events || [])],
                      };
                      newSeason.events[eventIndex].eventName = editEventName;
                      handler.runRequest(
                        updateSeason(newSeason),
                        "Updating event name...",
                        "Event name saved!",
                        "EVENT_NAME"
                      );
                      setEditingEvent(false);
                    }
                  }}
                >
                  Save
                </button>
              }
            />
          </div>
        )}
        <Grid
          container
          style={{ padding: 10, position: "relative", minHeight: 48 }}
        >
          <Grid>
            {!showSettings && (
              <>
                <Link
                  className="clickable"
                  style={{ marginRight: 20, width: 130 }}
                  to={`/sk/${seasonId}/teams`}
                >
                  Teams
                </Link>
                {!showStatsButtons && (
                  <button
                    className="clickable"
                    style={{ marginRight: 20, width: 130 }}
                    onClick={() => setShowStatsButtons(true)}
                  >
                    Stats
                  </button>
                )}
                {event.quizzes?.length === 0 && (
                  <button
                    className="link"
                    style={{ marginRight: 20 }}
                    onClick={() => {
                      if (!selectedSeason) return;
                      const newSeason: skTypes.Season = {
                        ...selectedSeason,
                      };
                      const newEvents = [...newSeason.events];
                      const index = newEvents.findIndex(
                        (ev) => ev.eventId === event.eventId
                      );
                      if (index >= 0) newEvents.splice(index, 1);
                      newSeason.events = newEvents;
                      handler.runRequest(
                        updateSeason(newSeason),
                        "Deleting this event...",
                        "Event deleted",
                        "EVENT_DELETE"
                      );
                    }}
                  >
                    Delete Event
                  </button>
                )}
              </>
            )}
          </Grid>
          <Grid offset="auto">
            <span className="important-information">
              {`${
                settings.touchscreen ? "Touchscreen" : "Mouse/Keyboard"
              } Mode`}
            </span>
          </Grid>
        </Grid>
        {showStatsButtons && (
          <Row style={{ padding: 10 }}>
            <Link
              className="clickable"
              style={{ marginRight: 20 }}
              to={`/sk/${seasonId}/quizzerstats`}
            >
              Quizzer Stats
            </Link>
            {!selectedSeason?.isPractice && (
              <Link
                className="clickable"
                style={{ marginRight: 20 }}
                to={`/sk/${seasonId}/teamstats`}
              >
                Team Stats
              </Link>
            )}
            {settings.skAdvanced && (
              <Link
                className="clickable"
                style={{ marginRight: 20 }}
                to={`/sk/${seasonId}/chapterstats`}
              >
                Chapter Stats
              </Link>
            )}
          </Row>
        )}

        {!showSettings && (
          <>
            <hr style={{ marginLeft: -10, width: "calc(100% + 20px)" }} />
            {selectedSeason.teams.length === 0 && (
              <p>To start a quiz, add at least one team.</p>
            )}
            {event.quizzes.length > 0 && (
              <h3 style={{ fontSize: 22, marginBottom: 20 }}>Quizzes</h3>
            )}
            <Grid container style={{ marginTop: 20 }} spacing={2}>
              {selectedSeason.teams.length > 0 && (
                <Grid size={quizBreakpoints}>
                  <Link
                    className="quiz-clickable"
                    to={`/sk/${seasonId}/${eventId}/${
                      selectedSeason?.isPractice ? "newpracticequiz" : "newquiz"
                    }`}
                    style={{ paddingTop: 20 }}
                  >
                    <span
                      style={{ fontWeight: "bold", textTransform: "uppercase" }}
                    >
                      New Quiz
                    </span>
                    <br />
                    <span style={{ fontSize: 54 }}>+</span>
                  </Link>
                </Grid>
              )}
              {[...event.quizzes]
                .sort((q1, q2) => {
                  if (q1.round !== q2.round) return q1.round - q2.round;
                  if (q1.site !== q2.site) return q1.site - q2.site;
                  if (!q1.createdTimeStamp || !q2.createdTimeStamp) return 0;
                  return q1.createdTimeStamp.localeCompare(q2.createdTimeStamp);
                })
                .map((quiz: skTypes.QuizSummary) => (
                  <Grid key={quiz.quizId} size={quizBreakpoints}>
                    <Link
                      className="quiz-clickable"
                      to={`/sk/${seasonId}/${eventId}/${quiz.quizId}`}
                      style={{ overflow: "hidden" }}
                      onClick={() => window.scrollTo(0, 0)}
                    >
                      <div className="no-wrap-text">{quiz.quizName}</div>
                      <div
                        className="no-wrap-text"
                        style={{ marginBottom: 10 }}
                      >{`Round ${quiz.round}, Site ${quiz.site}`}</div>
                      {!quiz.isPractice ? (
                        quiz.teams.map((team: skTypes.Team) => (
                          <div className="no-wrap-text" key={team.teamId}>
                            {team.nickname || team.teamName}
                          </div>
                        ))
                      ) : (
                        <>
                          {quiz.teams[0]?.quizzers
                            .filter((q, i) => i < 3)
                            .map((q) => (
                              <div key={q.quizzerId} className="no-wrap-text">
                                {q.quizzerName}
                              </div>
                            ))}
                          {quiz.teams[0].quizzers.length > 3 && (
                            <div>{`(+${
                              quiz.teams.length * 5 +
                              quiz.teams[quiz.teams.length - 1].quizzers
                                .length -
                              8
                            })`}</div>
                          )}
                        </>
                      )}
                    </Link>
                  </Grid>
                ))}
            </Grid>
          </>
        )}
      </div>
      {showSettings && <SKSettings onGoBack={() => setShowSettings(false)} />}
    </div>
  );
}
